import React, { useEffect, useState } from 'react';
import { useLocation, useRoute } from 'wouter';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { supabase } from '../../supabaseClient';
import FileUpload from '@/components/ui/fileupload';




const CandidateDetails = () => {
  const [match, params] = useRoute('/candidate-details/:candidateId');
  const [, navigate] = useLocation();
  const [candidate, setCandidate] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formValues, setFormValues] = useState({ name: '', phone: '', email: '', location: '' ,skills:'',experience:'',status:'',notice_period:'',linkedInUrl:'',current_ctc:'',expected_ctc:''});
  
  const candidateId = params?.candidateId;
  const handleAddRole = () => navigate(`/search-roles/${candidateId}`);
 
  useEffect(() => {
    const fetchCandidateDetails = async () => {
      if (!candidateId) {
        setError('Candidate ID is missing.');
        setLoading(false);
        return;
      }

      try {
        const { data: candidateData, error: candidateError } = await supabase
          .from('candidates')
          .select('*')
          .eq('id', candidateId)
          .single();

        if (candidateError) {
          setError(candidateError.message);
        } else {
          setCandidate(candidateData);
          setFormValues({
            name: candidateData.name,
            phone: candidateData.phone,
            email: candidateData.email,
            location: candidateData.location,
            skills: candidateData.skills,
            experience: candidateData.experience,
            status: candidateData.status,
            notice_period: candidateData.notice_period,
            linkedInUrl: candidateData.linkedInUrl,
            current_ctc: candidateData.current_ctc,
            expected_ctc: candidateData.expected_ctc,
          });
          // Fetch roles assigned to the candidate
          const { data: roleData, error: roleError } = await supabase
            .from('project_candidate')
            .select('project_id, projects(title)')
            .eq('candidate_id', candidateId);

          if (roleError) {
            setError(roleError.message);
          } else {
            setRoles(
              roleData.map((item) => ({
                id: item.project_id,
                title: item.projects?.title || 'Untitled project',
              }))
            );
          }
        }
      } catch (err) {
        setError('Error fetching candidate details.');
      } finally {
        setLoading(false);
      }
    };

    fetchCandidateDetails();
  }, [candidateId]);
  const handleDialogOpen = () => setDialogOpen(true);
  const handleDialogClose = () => setDialogOpen(false);
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleBack = () => window.history.back();

  const handleUpdate = async () => {
      try {
        const updatedValues = {
          name: formValues.name,
          phone: formValues.phone,
          email: formValues.email,
          location: formValues.location,
          skills: Array.isArray(formValues.skills) ? formValues.skills : [], 
          experience: formValues.experience && !isNaN(parseInt(formValues.experience)) ? parseInt(formValues.experience) : null, 
          status: formValues.status,
          notice_period: formValues.notice_period,
          linkedInUrl: formValues.linkedInUrl,
          current_ctc: formValues.current_ctc,
          expected_ctc: formValues.expected_ctc,
        };
        
        const { data, error } = await supabase
          .from('candidates')
          .update(updatedValues)
          .eq('id', candidateId)
          .select(); 
    
        if (error) {
          setError(error.message);
        } else {
          setCandidate(data[0]); 
          setDialogOpen(false);
        }
      } catch (err) {
        setError('Error updating candidate details.');
      }
    };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  if (!candidate) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography>No candidate details found.</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
          Candidate Details
        </Typography>
        <Box>
          <Button
                      variant="contained"
                      onClick={handleDialogOpen}
                      sx={{
                        backgroundColor: 'black',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#333',
                        },
                        mr: 1,
                      }}
                    >
                      Update
           </Button>
          <Button
            variant="contained"
            onClick={handleBack}
            sx={{
              backgroundColor: 'black',
              color: 'white',
              '&:hover': {
                backgroundColor: '#333',
              },
            }}
          >
            Back
          </Button>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            {candidate?.name}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
             <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Email:
              </Typography>
              <Typography variant="body1">{candidate.email}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Phone:
              </Typography>
              <Typography variant="body1">{candidate.phone}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Location:
              </Typography>
              <Typography variant="body1">{candidate.location}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Skills:
              </Typography>
              <Typography variant="body1">{candidate.skills}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Experience:
              </Typography>
              <Typography variant="body1">{candidate.experience}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Source:
              </Typography>
              <Typography variant="body1">{candidate.source}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Status:
              </Typography>
              <Typography variant="body1">{candidate.status}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                LinkedIn URL:
              </Typography>
              <Typography variant="body1">
                <a
                  href={candidate.linkedInUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#1976d2', textDecoration: 'none' }}
                >
                  {candidate.linkedInUrl}
                </a>
            </Typography>
            
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Current CTC
              </Typography>
              <Typography variant="body1">{candidate.current_ctc}</Typography>
            </Grid><Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Expected CTC
              </Typography>
              <Typography variant="body1">{candidate.expected_ctc}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Notie Period:
              </Typography>
              <Typography variant="body1">{candidate.notice_period}</Typography>
            </Grid>
            <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mt={2}>
                        <Box>
                          <Button
                            variant="contained"
                            onClick={handleAddRole}
                            sx={{
                              backgroundColor: 'black',
                              color: 'white',
                              '&:hover': {
                                backgroundColor: '#333',
                              },
                            }}
                          >
                            Add Role
                          </Button>
                        </Box>
                      </Box>
                <Typography variant="h6" sx={{ mt: 3, fontWeight: 'bold' }}>
                          Assigned Roles
                </Typography>
                <br />
              <List>
                {roles.map((role) => (
                  <ListItem
                    key={role.id}
                    onClick={() => navigate(`/role-details/${role.id}`)}
                  >
                    <ListItemText primary={role.title} />
                  </ListItem>
                ))}
              </List>
              <FileUpload basepath={'candidate/'+candidate.id+'/'}/>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      {/* Update Dialog */}
            <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth maxWidth="sm">
              <DialogTitle>Update Agency Details</DialogTitle>
              <DialogContent>
              <TextField
                  label="Name"
                  name="name"
                  fullWidth
                  margin="normal"
                  value={formValues.name}
                  onChange={handleInputChange}
                />

                <TextField
                  label="Phone"
                  name="phone"
                  fullWidth
                  margin="normal"
                  value={formValues.phone}
                  onChange={handleInputChange}
                />

                <TextField
                  label="Email"
                  name="email"
                  fullWidth
                  margin="normal"
                  value={formValues.email}
                  onChange={handleInputChange}
                />

                <TextField
                  label="Location"
                  name="location"
                  fullWidth
                  margin="normal"
                  value={formValues.location}
                  onChange={handleInputChange}
                />

                <TextField
                  label="Skills"
                  name="skills"
                  fullWidth
                  margin="normal"
                  value={formValues.skills}
                  onChange={handleInputChange}
                />

                <TextField
                  label="Experience"
                  name="experience"
                  fullWidth
                  margin="normal"
                  value={formValues.experience}
                  onChange={handleInputChange}
                />

 <TextField
            select
            label="Status"
            name="status"
            fullWidth
            margin="normal"
            value={formValues.status || ''}
            onChange={handleInputChange}
            SelectProps={{
              native: true,
            }}
          >
            <option value="" disabled>
              Select a Status
            </option>
            
              <option>
                Active
              </option>
              <option>Inactive</option>
          
          </TextField>

                <TextField
                  label="Notice Period"
                  name="notice_period"
                  fullWidth
                  margin="normal"
                  value={formValues.notice_period}
                  onChange={handleInputChange}
          />
           <TextField
                  label="Current CTC"
                  name="current_ctc"
                  fullWidth
                  margin="normal"
                  value={formValues.current_ctc}
                  onChange={handleInputChange}
                />
                 <TextField
                  label="Expected CTC"
                  name="expected_ctc"
                  fullWidth
                  margin="normal"
                  value={formValues.expected_ctc}
                  onChange={handleInputChange}
                />

                <TextField
                  label="LinkedIn URL"
                  name="linkedInUrl"
                  fullWidth
                  margin="normal"
                  value={formValues.linkedInUrl}
                  onChange={handleInputChange}
                />

              </DialogContent>
              <DialogActions>
                <Button onClick={handleDialogClose} color="secondary"
                 sx={{
                  backgroundColor: 'black',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#333',
                  },
                  mr: 1,
                }}>
                  Cancel
                </Button>
                <Button onClick={handleUpdate} color="primary" variant="contained"
                 sx={{
                  backgroundColor: 'black',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#333',
                  },
                  mr: 1,
                }}>
                  Save
                </Button>
              </DialogActions>
            </Dialog>
    </Container>
  );
};

export default CandidateDetails;
